import { getEventName } from '../../utils/eventHelpers';
import React from 'react';
import { BetItem } from '../BettingSlipDrawer';
import { TicketOut, CashoutQuoteOut } from '../../api/types';
import { useTranslation } from '../../i18n';
import { useLiveScores } from '../../hooks/useLiveScores';
import { isOpenForCashout } from '../../utils/ticketHelpers';
import { removeCustomBetSelection } from '../../utils/customBet';
import { choose } from '../../utils/combinations';
import { requestCashoutQuote, executeCashout, getEvent } from '../../api/api';
import { resolveOutcomeName, parseSpecifiers, resolveMarketName } from '../../utils/marketTranslations';
import { resolveMarketAndOutcome, legStatusIcon, legStatusLabel } from '../DesktopMyBetsPage';

export interface DesktopRightSidebarProps {
  bets: BetItem[];
  tickets: TicketOut[];
  rightSidebarTab: 'wettschein' | 'offene';
  setRightSidebarTab: (tab: 'wettschein' | 'offene') => void;
  onClearAll: () => void;
  onRemoveBet: (id: string) => void;
  onUpdateBet?: (id: string, patch: Partial<BetItem>) => void;
  totalOdds: string | React.ReactNode;
  stake: string;
  setStake: (val: string) => void;
  possibleWin: string;
  betStatus: 'idle' | 'success' | 'error';
  betError: string;
  isLogged: boolean;
  onLoginClick: () => void;
  handlePlaceBet: () => void;
  placing: boolean;
  stakeNum: number;
  // New props
  betslipMode: 'single' | 'combi' | 'system';
  setBetslipMode: (mode: 'single' | 'combi' | 'system') => void;
  individualStakes: Record<string, string>;
  setIndividualStakes: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  systemSize: number;
  setSystemSize: (size: number) => void;
}

export const DesktopRightSidebar: React.FC<DesktopRightSidebarProps> = ({
  bets, tickets, rightSidebarTab, setRightSidebarTab, onClearAll, onRemoveBet, onUpdateBet,
  totalOdds, stake, setStake, possibleWin, betStatus, betError, isLogged,
  onLoginClick, handlePlaceBet, placing, stakeNum,
  betslipMode, setBetslipMode, individualStakes, setIndividualStakes, systemSize, setSystemSize
}) => {
  const { t: translate, locale } = useTranslation();



  const hasLockedBet = React.useMemo(() => bets.some(b => b.isLocked), [bets]);

  const [cashoutQuotes, setCashoutQuotes] = React.useState<Record<string, CashoutQuoteOut>>({});
  const [cashoutLoading, setCashoutLoading] = React.useState<Record<string, boolean>>({});
  const [cashoutErrors, setCashoutErrors] = React.useState<Record<string, string>>({});
  const [cashoutConfirming, setCashoutConfirming] = React.useState<Record<string, boolean>>({});
  const [eventNames, setEventNames] = React.useState<Record<string, string>>({});
  const liveScores = useLiveScores(tickets);

  React.useEffect(() => {
    if (tickets.length === 0) return;

    const namesFromApi: Record<string, string> = {};
    tickets.forEach(t => t.legs?.forEach(l => {
      if (l.event_name) {
        namesFromApi[l.event_id] = l.event_name;
      }
    }));

    if (Object.keys(namesFromApi).length > 0) {
      setEventNames(prev => ({ ...prev, ...namesFromApi }));
    }
  }, [tickets]);

  React.useEffect(() => {
    if (!isLogged) return;
    const openTickets = tickets.filter(t => isOpenForCashout(t));
    if (openTickets.length === 0) return;

    const fetchQuotes = async () => {
      openTickets.forEach(t => {
        if (cashoutConfirming[t.id] || cashoutLoading[t.id]) return;
        requestCashoutQuote(t.id).then(quote => {
          setCashoutQuotes(prev => ({ ...prev, [t.id]: quote }));
        }).catch(() => {
          setCashoutQuotes(prev => {
            if (!prev[t.id]) return prev;
            const next = { ...prev };
            delete next[t.id];
            return next;
          });
        });
      });
    };

    fetchQuotes();
    const intervalId = setInterval(fetchQuotes, 5000);
    return () => clearInterval(intervalId);
  }, [tickets, isLogged, cashoutConfirming, cashoutLoading]);

  const handleRequestQuote = async (ticketId: string) => {
    setCashoutLoading(prev => ({ ...prev, [ticketId]: true }));
    setCashoutErrors(prev => ({ ...prev, [ticketId]: '' }));
    try {
      const quote = await requestCashoutQuote(ticketId);
      setCashoutQuotes(prev => ({ ...prev, [ticketId]: quote }));
    } catch (err: any) {
      setCashoutErrors(prev => ({ ...prev, [ticketId]: err.message || translate('slip.errorRequestingQuote') }));
    } finally {
      setCashoutLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  const handleConfirmCashout = async (ticketId: string, quoteId: string) => {
    setCashoutLoading(prev => ({ ...prev, [ticketId]: true }));
    setCashoutErrors(prev => ({ ...prev, [ticketId]: '' }));
    try {
      await executeCashout(quoteId);
      setCashoutQuotes(prev => {
        const next = { ...prev };
        delete next[ticketId];
        return next;
      });
      setCashoutConfirming(prev => ({ ...prev, [ticketId]: false }));
      window.dispatchEvent(new Event('balance-changed'));
    } catch (err: any) {
      setCashoutErrors(prev => ({ ...prev, [ticketId]: err.message || translate('slip.cashoutFailed') }));
    } finally {
      setCashoutLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  const handleCancelCashout = (ticketId: string) => {
    setCashoutConfirming(prev => ({ ...prev, [ticketId]: false }));
    setCashoutErrors(prev => ({ ...prev, [ticketId]: '' }));
  };
  return (
    <aside className="w-[320px] flex-shrink-0 sticky top-[80px] h-[calc(100vh-100px)] flex flex-col gap-4 transition-all duration-300">
      <div className="bg-white dark:bg-[#1a1a1a] dark:text-white rounded-lg shadow-lg overflow-hidden flex flex-col border border-gray-100 dark:border-[#333] relative h-[calc(100vh-120px)]">
        


        <div className="flex bg-white dark:bg-[#1a1a1a] dark:text-white">
          <div 
            onClick={() => setRightSidebarTab('wettschein')}
            className={`flex-1 py-3 text-center cursor-pointer transition-all ${rightSidebarTab === 'wettschein' ? 'border-b-[3px] border-tipico-red' : 'border-b-[3px] border-transparent hover:bg-gray-50 dark:bg-[#252525]'}`}
          >
            <div className="text-[22px] font-bold text-gray-900 dark:text-white leading-none mb-1">{bets.length}</div>
            <div className={`text-[11px] ${rightSidebarTab === 'wettschein' ? 'text-gray-900 dark:text-white font-bold' : 'text-gray-500 dark:text-gray-400'}`}>{translate('slip.title')}</div>
          </div>
          <div 
            onClick={() => setRightSidebarTab('offene')}
            className={`flex-1 py-3 text-center cursor-pointer transition-all ${rightSidebarTab === 'offene' ? 'border-b-[3px] border-tipico-red' : 'border-b-[3px] border-transparent hover:bg-gray-50 dark:bg-[#252525]'}`}
          >
            <div className="text-[22px] font-bold text-gray-900 dark:text-white leading-none mb-1">{tickets.filter(t => isOpenForCashout(t)).length}</div>
            <div className={`text-[11px] ${rightSidebarTab === 'offene' ? 'text-gray-900 dark:text-white font-bold' : 'text-gray-500 dark:text-gray-400'}`}>{translate('slip.openBets')}</div>
          </div>
        </div>

        {/* Mode Switcher Tabs */}
        {rightSidebarTab === 'wettschein' && bets.length > 0 && (
          <div className="flex border-b border-gray-100 dark:border-[#333] bg-white dark:bg-[#1a1a1a] dark:text-white px-1 py-1 gap-1 flex-shrink-0">
            {(['single', 'combi', 'system'] as const).map((mode) => {
              const disabled = mode === 'system' && bets.length < 3;
              const active = betslipMode === mode;
              const label = mode === 'single' ? translate('slip.single') : mode === 'combi' ? translate('slip.combi') : translate('slip.system');
              return (
                <button
                  key={mode}
                  disabled={disabled}
                  onClick={() => setBetslipMode(mode)}
                  className={`flex-1 py-1.5 text-center text-xs font-bold rounded transition-all cursor-pointer ${
                    active
                      ? 'bg-tipico-red text-white'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200 bg-transparent disabled:opacity-30 disabled:hover:text-gray-500 dark:text-gray-400 disabled:cursor-not-allowed'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
        
        <div className="flex-1 overflow-y-auto min-h-0 bg-gray-50 dark:bg-[#252525]">
          {rightSidebarTab === 'wettschein' && (
            bets.length === 0 ? (
              betStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 px-6 text-center bg-white dark:bg-[#1a1a1a] dark:text-white h-full min-h-[400px]">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-5 shadow-sm">
                    <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-[18px] text-green-600 font-black mb-2 uppercase tracking-wide">{translate('slip.betSuccess')}</p>
                  <p className="text-[14px] text-gray-500 dark:text-gray-400 font-medium">{translate('slip.betSuccessDesc')}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 px-6 text-center bg-white dark:bg-[#1a1a1a] dark:text-white h-full min-h-[400px]">
                  <svg className="w-10 h-10 text-gray-300 mb-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4 2v20l2-2 2 2 2-2 2 2 2-2 2 2 2-2 2 2 2-2 2 2V2H4zm14 16H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V8h12v2z" />
                  </svg>
                  <p className="text-[13px] text-gray-400 font-medium">{translate('slip.empty')}</p>
                  <p className="text-[13px] text-gray-400 font-medium mt-1">{translate('slip.emptyHint')}</p>
                </div>
              )
            ) : (
              <>
                <div className="flex flex-col bg-white dark:bg-[#1a1a1a] dark:text-white border-b border-gray-100 dark:border-[#333]">
                  {/* Clean row with clear-all button and system size selector */}
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">
                      {betslipMode === 'single' ? translate('slip.single') : betslipMode === 'combi' ? translate('slip.combi') : translate('slip.system')}
                    </span>
                    <button onClick={onClearAll} className="text-gray-400 hover:text-gray-700 dark:text-gray-300 transition">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* System combination size selector */}
                  {betslipMode === 'system' && (
                    <div className="px-3 pb-2.5 pt-0.5 flex flex-wrap items-center gap-1 bg-gray-50 dark:bg-[#252525] border-t border-gray-100 dark:border-[#333]">
                      <span className="text-[10px] text-gray-400 font-semibold uppercase mr-1">System size:</span>
                      {Array.from({ length: bets.length - 1 }, (_, i) => i + 2).map((k) => (
                        <button
                          key={k}
                          onClick={() => setSystemSize(k)}
                          className={`px-1.5 py-0.5 text-[11px] font-extrabold border rounded transition-all cursor-pointer ${
                            systemSize === k
                              ? 'bg-gray-800 border-gray-800 text-white'
                              : 'bg-white dark:bg-[#1a1a1a] dark:text-white border-gray-300 text-gray-700 dark:text-gray-300 hover:border-gray-400 hover:bg-gray-100'
                          }`}
                        >
                          {k}/{bets.length}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {bets.map((bet) => (
                  <div key={bet.id} className="border-b border-gray-100 dark:border-[#333] flex items-stretch last:border-0 hover:bg-gray-50 dark:bg-[#252525] bg-white dark:bg-[#1a1a1a] dark:text-white flex-row">
                    <div className="w-[30px] flex items-start justify-center pt-3 border-r border-gray-50">
                      <div className="w-4 h-4 border border-gray-300 text-gray-300 text-[9px] font-bold flex items-center justify-center rounded-sm">B</div>
                    </div>
                    <div className="flex-1 px-3 py-3 font-sans">
                      <div className="flex justify-between items-start mb-0.5">
                        <div className="text-[12px] text-gray-900 dark:text-white pr-2">
                          {bet.isCustomBet ? (
                            /* MK+ / CustomBet: one provider-priced same-event group —
                               its marketId/outcomeId only point at the first selection. */
                            <span className="flex items-center gap-1.5">
                              <span className="text-[10px] font-black text-white bg-[#c8102e] px-1.5 py-0.5 rounded leading-none">MK+</span>
                              <span className="text-[13px] font-bold text-gray-900 dark:text-white">{bet.tippLabel}</span>
                            </span>
                          ) : (
                            <>
                              <span className="text-gray-500 dark:text-gray-400 font-normal">{bet.marketId ? resolveMarketName(bet.marketId, locale, parseSpecifiers(bet.specifiers), undefined, bet.matchName) || 'Tipp' : 'Tipp'}: </span><span className="font-bold">{bet.marketId && bet.outcomeId ? resolveOutcomeName(bet.marketId, bet.outcomeId, locale, parseSpecifiers(bet.specifiers), bet.matchName, bet.outcomeName) : bet.tippLabel}</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-sm font-bold text-gray-800 dark:text-gray-200 flex items-center justify-center px-1.5 py-0.5 rounded transition-colors duration-500 ${bet.oddsTrend === 'up' ? 'bg-green-200 dark:bg-green-900' : bet.oddsTrend === 'down' ? 'bg-red-200 dark:bg-red-900' : ''}`}>
                            {bet.isLocked ? (
                              <svg className="w-4 h-4 text-gray-400 block" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              bet.odds || '-'
                            )}
                          </span>
                          <button onClick={() => onRemoveBet(bet.id)} className="text-gray-300 hover:text-gray-600 dark:text-gray-400 transition -mt-0.5">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate leading-snug">{bet.matchName}</div>

                      {/* MK+ legs — each one can be dropped, which re-prices the group */}
                      {bet.isCustomBet && (bet.customBetSelections?.length ?? 0) > 0 && (
                        <div className="mt-2 flex flex-col">
                          {bet.customBetSelections!.map((sel, i) => {
                            const isLast = i === bet.customBetSelections!.length - 1;
                            return (
                              <div key={`${sel.market_id}-${sel.specifiers}-${sel.outcome_id}-${i}`} className="relative flex items-start justify-between pb-2.5 last:pb-0">
                                {/* Timeline connector line */}
                                {!isLast && (
                                  <div className="absolute left-[4px] top-[10px] bottom-[-10px] w-[1px] bg-[#d8dce0] dark:bg-[#444] z-0"></div>
                                )}
                                
                                {/* Timeline circle */}
                                <div className="absolute left-0 top-[6.5px] w-[9px] h-[9px] rounded-full border border-[#d8dce0] dark:border-[#444] bg-white dark:bg-[#1a1a1a] z-10"></div>
                                
                                <div className="pl-[20px] flex-1 flex items-start justify-between gap-2 min-w-0">
                                  <span className="text-[12px] leading-snug text-gray-700 dark:text-gray-300 truncate flex-1">
                                    <span className="text-gray-500 dark:text-gray-400">{sel.market_name || 'Tipp'}: </span>
                                    <span className="font-bold text-gray-900 dark:text-white">{sel.outcome_name || sel.outcome_id}</span>
                                  </span>
                                  <button
                                    onClick={() => onUpdateBet && removeCustomBetSelection(bet, i, { locale, onUpdateBet, onRemoveBet })}
                                    className="text-gray-300 hover:text-gray-600 dark:text-gray-400 transition flex-shrink-0 mt-0.5"
                                  >
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {betslipMode === 'single' && (
                        <div className="mt-2 flex items-center justify-end gap-1.5">
                          <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">{translate('slip.stake')}:</span>
                          <div className="relative w-16">
                            <input
                              type="number"
                              value={individualStakes[bet.id] || stake}
                              onChange={(e) => {
                                const val = e.target.value;
                                setIndividualStakes((prev) => ({ ...prev, [bet.id]: val }));
                              }}
                              className="w-full text-right text-[11px] font-bold text-gray-800 dark:text-gray-200 border dark:border-[#444] rounded px-1.5 py-0.5 pr-4 outline-none focus:border-tipico-red bg-white dark:bg-[#1a1a1a] dark:text-white dark:focus:bg-[#252525]"
                              min="0"
                            />
                            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] font-bold text-gray-400">€</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )
          )}
          
          {rightSidebarTab === 'offene' && (
            tickets.filter(t => isOpenForCashout(t) && t.ticket_type !== 'system').length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-6 text-center bg-white dark:bg-[#1a1a1a] dark:text-white">
                <p className="text-[13px] text-gray-400 font-medium">{translate('slip.noOpenBets')}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2 p-2">
                {tickets.filter(t => isOpenForCashout(t) && t.ticket_type !== 'system').map(t => (
                  <div key={t.id} className="p-3 border border-gray-200 dark:border-[#333] rounded shadow-sm bg-white dark:bg-[#1a1a1a] dark:text-white">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{t.ticket_type === 'system' ? 'System' : t.ticket_type === 'acca' ? (t.multi_way_mode === 'provider_custom_bet' ? 'Betbuilder+' : translate('slip.combiBet')) : translate('slip.singleBet')}</span>
                      <span className="text-xs font-bold text-gray-900 dark:text-white">{parseFloat(t.total_stake).toFixed(2).replace('.', ',')} €</span>
                    </div>
                    <div className="text-[10px] text-gray-400 mb-1">ID: {t.ticket_reference || t.id}</div>
                    <div className="flex justify-between items-center text-[10px] text-gray-500 dark:text-gray-400 mb-2 border-b border-gray-100 dark:border-[#333] pb-2">
                      <span>{translate('slip.totalOdds')}: {parseFloat(t.accepted_total_odds).toFixed(2).replace('.', ',')}</span>
                      <span className="text-green-600 font-bold">{translate('slip.potentialWinShort')}: {(t.potential_payout_is_exact === false ? '~' : '') + parseFloat(t.potential_payout).toFixed(2).replace('.', ',')} €</span>
                    </div>

                    <div className="flex flex-col gap-2 mb-2">
                      {(() => {
                        const legsByEvent: Record<string, any[]> = {};
                        t.legs?.forEach((leg: any) => {
                          if (!legsByEvent[leg.event_id]) legsByEvent[leg.event_id] = [];
                          legsByEvent[leg.event_id].push(leg);
                        });

                        return Object.values(legsByEvent).map((group, groupIdx) => {
                          const firstLeg = group[0];
                          const event = t.events?.find(e => e.event_id === firstLeg.event_id);
                          const fallbackName = eventNames[firstLeg.event_id];
                          const eventName = (event?.event?.home?.name && event?.event?.away?.name) ? getEventName(event.event) : fallbackName;
                          const displayName = eventName || `Event ${firstLeg.event_id.split(':').pop()}`;

                          const isMatchKombi = t.multi_way_mode === 'provider_custom_bet' && group.length > 1;

                          if (!isMatchKombi) {
                            return (
                              <React.Fragment key={groupIdx}>
                                {group.map((leg, legIdx) => {
                                  const { marketName, outcomeName } = resolveMarketAndOutcome(leg, displayName, locale);

                                  return (
                                    <div key={leg.id || `${groupIdx}-${legIdx}`} className="flex items-start gap-2 border-b border-dashed border-gray-100 dark:border-[#333] pb-2 last:border-0 last:pb-0 mb-2 last:mb-0">
                                      <div className="scale-75 origin-top-left -ml-1 mt-0.5">
                                        {legStatusIcon(leg)}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-[11px] font-bold text-gray-900 dark:text-white truncate leading-tight flex items-center gap-1.5">
                                          {displayName}
                                          {liveScores[leg.event_id] && (
                                            <span className="text-tipico-red font-black text-[10px]">
                                              [{liveScores[leg.event_id].home_score}-{liveScores[leg.event_id].away_score}]
                                              {liveScores[leg.event_id].clock && <span className="ml-0.5 text-gray-500 dark:text-gray-400 font-medium">({liveScores[leg.event_id].clock?.split(':')[0]}')</span>}
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">
                                          {marketName}
                                          <span className="mx-1 text-gray-300">›</span>
                                          <span className="font-semibold text-gray-700 dark:text-gray-300">{outcomeName}</span>
                                        </div>
                                        {leg.result_status && leg.result_status !== 'not_resulted' && leg.result_status !== 'pending' && leg.result_status !== 'open' && leg.result_status !== 'won' && leg.result_status !== 'win' && leg.result_status !== 'lost' && leg.result_status !== 'lose' && (
                                          <div className="text-[11px] text-gray-400 mt-1 bg-gray-50 dark:bg-[#252525] px-1.5 py-0.5 rounded inline-block font-medium">
                                            {legStatusLabel(leg)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </React.Fragment>
                            );
                          }

                          // MatchKombi grouped rendering
                          return (
                            <div key={`mk-${firstLeg.event_id}`} className="mb-2 last:mb-0 border border-gray-200 dark:border-[#333] rounded overflow-hidden shadow-sm">
                              <div className="bg-gray-50 dark:bg-[#252525] px-2 py-2 border-b border-gray-200 dark:border-[#333] flex flex-col gap-1">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[9px] font-black text-white bg-[#c8102e] px-1.5 py-0.5 rounded leading-none">MK+</span>
                                    <span className="text-[11px] font-bold text-gray-900 dark:text-white">{group.length}er MatchKombi</span>
                                  </div>
                                  {(() => {
                                    let mkOdds = t.event_groups?.find((g: any) => g.event_id === firstLeg.event_id && g.operator === 'all')?.accepted_odds;
                                    if (!mkOdds && t.legs?.length === group.length) {
                                      mkOdds = t.accepted_total_odds;
                                    }
                                    if (mkOdds) {
                                      const n = parseFloat(mkOdds);
                                      const formatted = isNaN(n) ? mkOdds : n.toFixed(2).replace('.', ',');
                                      return <span className="text-[11px] font-black text-gray-900 dark:text-white">{formatted}</span>;
                                    }
                                    return null;
                                  })()}
                                </div>
                                <div className="text-[10px] text-gray-500 dark:text-gray-400 font-medium truncate">
                                  {displayName}
                                </div>
                              </div>
                              <div className="bg-white dark:bg-[#1a1a1a] dark:text-white px-2 py-1.5 flex flex-col gap-1">
                                {group.map((leg, legIdx) => {
                                  const { marketName, outcomeName } = resolveMarketAndOutcome(leg, displayName, locale);
                                  return (
                                    <div key={leg.id || legIdx} className="flex items-start justify-between gap-2 text-[10px] leading-snug py-0.5 border-b border-dashed border-gray-100 dark:border-[#333] last:border-0">
                                      <div className="flex-1 text-gray-600 dark:text-gray-400 truncate">
                                        {marketName}: <span className="font-bold text-gray-900 dark:text-white">{outcomeName}</span>
                                      </div>
                                      <div className="scale-[0.7] origin-right">
                                        {legStatusIcon(leg)}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    {(() => {
                      const loading = cashoutLoading[t.id];
                      const quote = cashoutQuotes[t.id];
                      const err = cashoutErrors[t.id];
                      const confirming = cashoutConfirming[t.id];

                      if (quote && !confirming) {
                        return (
                          <div className="mt-2">
                            <button
                              disabled={loading}
                              onClick={() => setCashoutConfirming(prev => ({ ...prev, [t.id]: true }))}
                              className="w-full bg-[#00a650] hover:bg-[#009045] text-white text-[13px] font-bold py-2.5 rounded shadow-sm transition cursor-pointer flex items-center justify-center gap-2"
                            >
                              <span>Cashout</span>
                              <span>{parseFloat(quote.amount).toFixed(2).replace('.', ',')} €</span>
                            </button>
                            {err && <p className="text-[10px] text-red-500 text-center font-semibold mt-1">{err}</p>}
                          </div>
                        );
                      }

                      if (quote && confirming) {
                        return (
                          <div className="mt-2 flex flex-col gap-2 border-t border-gray-100 dark:border-[#333] pt-2">
                            <div className="flex items-center justify-between text-xs font-semibold text-gray-700 dark:text-gray-300">
                              <span>{translate('misc.confirmColon')}</span>
                              <span className="text-green-600 font-black text-sm">{parseFloat(quote.amount).toFixed(2).replace('.', ',')} €</span>
                            </div>
                            <div className="flex gap-2">
                              <button
                                disabled={loading}
                                onClick={() => handleConfirmCashout(t.id, quote.id)}
                                className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white text-[11px] font-bold py-2 rounded transition cursor-pointer text-center"
                              >
                                {loading ? '...' : translate('misc.confirm')}
                              </button>
                              <button
                                disabled={loading}
                                onClick={() => handleCancelCashout(t.id)}
                                className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-[#333] dark:hover:bg-[#444] disabled:opacity-50 text-gray-700 dark:text-gray-300 text-[11px] font-bold py-2 rounded transition cursor-pointer text-center"
                              >
                                {translate('misc.cancel')}
                              </button>
                            </div>
                            {err && <p className="text-[10px] text-red-500 text-center font-semibold mt-1">{err}</p>}
                          </div>
                        );
                      }

                        return null;
                    })()}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
        
        {rightSidebarTab === 'wettschein' && bets.length > 0 && (
          <div className="bg-white dark:bg-[#1a1a1a] dark:text-white border-t border-gray-200 dark:border-[#333] mt-2">
            <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100 dark:border-[#333]">
              {betslipMode === 'combi' ? (
                <>
                  <span className="text-[12px] font-bold text-gray-700 dark:text-gray-300">{translate('slip.totalOdds')}</span>
                  <span className="text-sm font-black text-gray-900 dark:text-white">{totalOdds}</span>
                </>
              ) : betslipMode === 'system' ? (
                <>
                  <span className="text-[12px] font-bold text-gray-700 dark:text-gray-300">{translate('slip.combinations')}</span>
                  <span className="text-sm font-black text-gray-900 dark:text-white">{choose(bets.length, systemSize)}</span>
                </>
              ) : (
                <div className="flex justify-between items-center mb-2 px-1">
                  <span className="text-[12px] font-bold text-gray-700 dark:text-gray-300">{translate('slip.singleBets')}</span>
                  <span className="text-[12px] font-bold text-gray-900 dark:text-white bg-gray-100 px-2 py-0.5 rounded-full">{bets.length}</span>
                </div>
              )}
            </div>
            <div className="px-3 py-3">
              {betslipMode === 'combi' ? (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 relative">
                     <input 
                       type="number" 
                       value={stake} 
                       onChange={(e) => setStake(e.target.value)} 
                       className="w-full text-right text-sm font-bold text-gray-800 dark:text-gray-200 border dark:border-[#444] focus:border-tipico-red rounded py-2 pr-7 pl-2 outline-none bg-white dark:bg-[#1a1a1a] dark:focus:border-tipico-red dark:focus:bg-[#252525] transition transition-shadow focus:shadow-inner" 
                       min="0" 
                       placeholder="0,00"
                     />
                     <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500 dark:text-gray-400">€</span>
                  </div>
                  <div className="w-[120px] text-right">
                    <p className="text-[9px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">{translate('slip.potentialWinShort')}</p>
                    <p className="text-base font-black text-green-600 leading-none">{possibleWin} €</p>
                  </div>
                </div>
              ) : betslipMode === 'system' ? (
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <p className="text-[9px] uppercase font-bold text-gray-400 mb-1">{translate('slip.stakePerWay')}</p>
                      <div className="relative">
                        <input 
                          type="number" 
                          value={stake} 
                          onChange={(e) => setStake(e.target.value)} 
                          className="w-full text-right text-sm font-bold text-gray-800 dark:text-gray-200 border dark:border-[#444] focus:border-tipico-red rounded py-2 pr-7 pl-2 outline-none bg-white dark:bg-[#1a1a1a] dark:focus:border-tipico-red dark:focus:bg-[#252525]" 
                          min="0" 
                          placeholder="0,00"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500 dark:text-gray-400">€</span>
                      </div>
                    </div>
                    <div className="w-[120px] text-right pt-4">
                      <p className="text-[9px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">{translate('slip.maxWin')}</p>
                      <p className="text-base font-black text-green-600 leading-none">{possibleWin} €</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#252525] px-2 py-1.5 rounded border border-gray-100 dark:border-[#333]">
                    <span>{translate('slip.totalStake')}:</span>
                    <span className="font-bold text-gray-800 dark:text-gray-200">{stakeNum.toFixed(2).replace('.', ',')} €</span>
                  </div>
                </div>
              ) : (
                /* Single mode */
                <div className="flex flex-col gap-2 mb-4">
                  <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#252525] px-2 py-1.5 rounded border border-gray-100 dark:border-[#333] mb-1">
                    <span>{translate('slip.totalStake')}:</span>
                    <span className="font-bold text-gray-800 dark:text-gray-200">{stakeNum.toFixed(2).replace('.', ',')} €</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-left">
                      <p className="text-[9px] uppercase font-bold tracking-wider text-gray-400">{translate('slip.quickStake')}</p>
                      <div className="relative w-28 mt-1">
                        <input 
                          type="number" 
                          value={stake} 
                          onChange={(e) => {
                            const val = e.target.value;
                            setStake(val);
                            const updated: Record<string, string> = {};
                            bets.forEach(b => { updated[b.id] = val; });
                            setIndividualStakes(updated);
                          }} 
                          className="w-full text-right text-xs font-bold text-gray-800 dark:text-gray-200 border focus:border-tipico-red rounded py-1.5 pr-6 pl-2 outline-none" 
                          min="0" 
                          placeholder="0,00"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500 dark:text-gray-400">€</span>
                      </div>
                    </div>
                    <div className="w-[120px] text-right">
                      <p className="text-[9px] uppercase font-bold tracking-wider text-gray-400 mb-0.5">{translate('slip.potentialWinShort')}</p>
                      <p className="text-base font-black text-green-600 leading-none">{possibleWin} €</p>
                    </div>
                  </div>
                </div>
              )}
              
              {hasLockedBet && (
                <p className="text-red-600 text-[11px] text-center font-bold mb-3 bg-red-50 py-1.5 px-2 rounded border border-red-200">
                  {translate('slip.hasLockedBets') || 'Ein oder mehrere Tipps sind gesperrt. Bitte entfernen.'}
                </p>
              )}
              
              {betStatus === 'error' && <p className="text-red-600 text-xs text-center font-bold mb-3 bg-red-50 py-1.5 rounded">{betError}</p>}
              
              {isLogged ? (
                <button
                  onClick={handlePlaceBet}
                  disabled={placing || stakeNum <= 0 || hasLockedBet}
                  className="w-full bg-[#00a650] text-white text-sm font-bold py-3 rounded uppercase tracking-wider hover:bg-[#009045] transition flex items-center justify-center cursor-pointer shadow disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {placing ? translate('slip.placingBtn') : translate('slip.placeBet')}
                </button>
              ) : (
                <button onClick={onLoginClick} className="w-full bg-tipico-red text-white text-sm font-bold py-3 rounded uppercase tracking-wider hover:bg-red-700 transition">Anmelden</button>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};